<template>
  <div class="card project-card">
    <div class="image-header">
      <b-img
        :src="project.logo || '/static/img/default/project-placeholder-new.png'"
        onerror=""
        :alt="project.logo_description || $gettext('Image showcasing the project')"
        :aria-label="project.title + $gettext(' Open project')"
        class="card-img-top pointer"
        @click="openProject"
        @keydown.enter="openProject"
        @keydown.space.prevent="openProject"
      />
      <FavoriteProjectButton
        class="favorite-project-btn"
        :project="project"
        :projectDetailSettings="projectDetailSettings"
      />
      <template v-if="project.logo_is_ai_generated">
        <span :id="project.slug + '-ai-badge'" class="ai-generated-label-wrapper" role="note">
          <AiGeneratedLabel variant="round" />
        </span>
        <b-tooltip :target="project.slug + '-ai-badge'" placement="top">
          <translate key="ai-generated">AI-generated</translate>
        </b-tooltip>
      </template>
      <span
        v-if="project.fully_funded && showDonationInfo"
        :id="project.slug + '-img-badge'"
        class="ended-badge btn-base-ci"
        tabindex="0"
        role="note"
        :aria-label="$gettext('Project status: Fully funded')"
      >
        <i class="far fa-check-circle" aria-hidden="true" />
        <translate key="fully-funded">Fully funded</translate>
      </span>
      <span
        v-else-if="project.expired && showDonationInfo"
        :id="project.slug + '-img-badge'"
        class="ended-badge btn-base-ci"
        tabindex="0"
        role="note"
        :aria-label="$gettext('Project status: Finished')"
      >
        <i class="far fa-clock" aria-hidden="true" />
        <translate key="finished">Finished</translate>
      </span>
    </div>
    <div v-if="!pageSettings.isVotingList && showDonationInfo" class="progress">
      <div
        role="progressbar"
        class="progress-bar social_bar"
        :aria-label="$gettext('Project funding progress') + ': ' + getProjectPercentage(project) + '%'"
        :aria-valuenow="getProjectPercentage(project)"
        :style="{ width: getProjectPercentage(project) + '%' }"
      />
    </div>
    <div class="card-body py-1">
      <span
        class="card-title pointer h4"
        :aria-label="getProjectTitleAndStatus"
        @click="openProject"
        @keydown.enter="openProject"
        @keydown.space.prevent="openProject"
      >
        {{ project.title }}
      </span>

      <p
        class="text-muted project-card-address"
        :aria-label="`${$gettext('Organization')}: ${project.organization.title}, ${$gettext('Location')}: ${project.organization.city}, ${project.organization.country}, ${$gettext('Category')}: ${project.category}`"
      >
        {{ project.organization.title }}<br />
        {{ project.organization.city }}, {{ project.organization.country }} |
        {{ project.category }}
      </p>
      <div
        v-if="!pageSettings.isVotingList && showDonationInfo"
        :aria-label="$gettext('Project funding details')"
        aria-describedby="project-amount-collected"
      >
        <div id="project-amount-collected">
          <span class="project-card-collected">
            <b class="collected-amount">{{ balance }} </b>
            <translate
              key="collected"
              :translate-params="
                pageSettings.currencyDisplay.useCurrency
                  ? { coinName: '' }
                  : { coinName: pageSettings.currencyDisplay.coinNamePlural }
              "
            >
              %{ coinName } collected</translate
            >
          </span>
          <br />

          <span class="project-card-percentage">
            <b class="collected-amount">{{ getProjectPercentage(project) }}% </b>
            <translate key="percentage-needs">of current need</translate>
          </span>
        </div>
      </div>
      <div
        v-else-if="pageSettings.displayCurrentVotingRank"
        class="d-flex justify-content-between mb-3 rounded-lg p-2 mx-n2 bg-light"
        :aria-label="$gettext('Project voting details')"
        aria-describedby="project-vote-details"
      >
        <div id="project-vote-details" class="d-flex justify-content-between w-100">
          <div class="project-card-votes">
            <span class="text-base-ci h5">{{ project.votes }}</span>
            <br />
            <span key="vote-count" v-translate>Votes received</span>
          </div>
          <div class="text-right project-card-voting-rank">
            <span class="text-base-ci h5">{{ project.voting_rank }}</span>
            <br />
            <span key="voting-rank" v-translate>Current rank</span>
          </div>
        </div>
      </div>
      <p class="mb-1" v-html="truncate(project.summary, 70)" />
    </div>
    <b-card-footer>
      <b-button class="w-100 view-project-btn" variant="base-ci" @click="openProject">
        <translate v-if="shouldDisplaySupportProjectText()"> View And Support Project </translate>
        <translate v-else>View Project</translate>
      </b-button>
    </b-card-footer>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator'

import AiGeneratedLabel from '@/components/explore/AiGeneratedLabel.vue'
import FavoriteProjectButton from '@/components/explore/FavoriteProjectButton.vue'
import LocaleMixin from '@/mixins/LocaleMixin'
import { ExploreModule } from '@/store/modules/explore'
import { IProjectDetailSettings } from '@/types/cms'
import { IPageSettings } from '@/types/cms'
import { IExploreProjectMinimal } from '@/types/projects'
import { ceilValueIfBelowX } from '@/utils/helpers/math'

@Component({
  name: 'project-card',
  components: {
    AiGeneratedLabel,
    FavoriteProjectButton,
  },
})
export default class ProjectCard extends Mixins(LocaleMixin) {
  @Prop() project!: IExploreProjectMinimal
  @Prop() pageSettings: IPageSettings

  imageHovered = false
  buttonHovered = false

  getProjectPercentage(project: IExploreProjectMinimal): number {
    return ceilValueIfBelowX((project.balance / project.goal) * 100, 99)
  }

  get getProjectTitleAndStatus(): string {
    let status = ''
    if (this.project.fully_funded && this.showDonationInfo) {
      status = this.$gettext('Project status: Fully funded')
    } else if (this.project.expired && this.showDonationInfo) {
      status = this.$gettext('Project status: Finished')
    }
    return `${this.project.title} ${status}`
  }

  get balance(): string {
    const { balance } = this.project

    return this.pageSettings.currencyDisplay.useCurrency
      ? this.toCurrency(balance, { currency: this.pageSettings.currencyDisplay.currencyIsoCode })
      : this.toCoins(balance, this.pageSettings.currencyDisplay.factorOfCurrencyToCoin)
  }

  get projectDetailSettings(): IProjectDetailSettings {
    return ExploreModule.projectDetailSettings
  }

  get showDonationInfo(): boolean {
    if (this.project?.is_status_archived) {
      return this.projectDetailSettings?.displayArchivedCoinBalance !== 3
    }
    return this.projectDetailSettings?.displayCurrentCoinBalance !== 3
  }

  truncate(title: string, length: number): string {
    if (title.length > length) {
      return `${title.slice(0, length - 3)} ...`
    }
    return title
  }

  shouldDisplaySupportProjectText() {
    let condition = false

    if (this.pageSettings.isVotingList) {
      condition =
        !this.project.fully_funded &&
        !this.project.expired &&
        !this.project.is_status_archived &&
        this.pageSettings.voting?.active
    } else {
      condition = !this.project.fully_funded && !this.project.expired && !this.project.is_status_archived
    }

    return condition
  }

  openProject() {
    // Filter out slug and preview_token from query params, as they are not needed in the project detail page and lead to 404
    const query = { ...this.$route.query, slug: undefined, preview_token: undefined }

    if (this.pageSettings.isVotingList) {
      if (this.pageSettings.voting) {
        this.$router.push({
          name: 'project-detail-voting',
          params: { projectSlug: this.project.slug, votingSlug: this.pageSettings.voting.slug },
          query,
        })
      } else {
        this.$router.push({
          name: 'project-detail-voting',
          params: { projectSlug: this.project.slug, votingSlug: this.project.current_voting },
          query,
        })
      }
    } else {
      this.$router.push({
        name: 'project-detail',
        params: { projectSlug: this.project.slug },
        query,
      })
    }

    if (this.pageSettings.singlePage) {
      // Scrollbehavior on router doesn't always work,
      // so we double down here to make sure the details are in view
      // window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }) <- doesn't work for some reason
      window.scrollTo(0, 0)
    } else {
      location.reload()
    }
  }
}
</script>
